import type { RawParams } from "@uirouter/core";

import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";

import type { ICampaignsScrapingConfiguration } from "app/components/campaigns/shared/CampaignsConst";
import {
    getLocaleForScrapingConfig,
    getLocationsIdsForSelectedCampaign,
} from "app/components/campaigns/shared/CampaignsUtils";
import { commonWebSources } from "app/components/filters-bar/utils";
import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type { IRankTrackerWidgetsSharedParams } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/shared/KeywordsReportTypes";
import { ESerpGroups } from "app/common/serp/SerpFilterMaps";
import type { RTRawParams } from "app/pages/digital-marketing/keywords-campaigns/reports/types";
import type { DatesRange } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { apiFormat } from "app/services/DurationService";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

const isScrapingConfAlignedWithSelectedDeviceAndLocations = (
    scrapingConf: ICampaignsScrapingConfiguration,
    device: string,
    selectedLocations: string[],
) =>
    device?.includes(scrapingConf.device.toString()) &&
    selectedLocations?.some((location) => location === getLocaleForScrapingConfig(scrapingConf));

export const getRelevantScrapingConfigIDsForSelectedDevice = (
    device: string,
    scrapingConfigs: ICampaignsScrapingConfiguration[],
    locationsIds: string,
): string => {
    const selectedLocations = locationsIds?.split("&");
    return scrapingConfigs
        ?.filter((scrapingConf) =>
            isScrapingConfAlignedWithSelectedDeviceAndLocations(scrapingConf, device, selectedLocations),
        )
        ?.map((scrapingConf) => scrapingConf.id)
        .join(",");
};

export const getFormattedDurationForWidgetSubtitle = (durationObject: DatesRange): string => {
    return `${durationObject?.from?.local().format(shortMonthDayAndYear)} - ${durationObject?.to
        ?.local()
        .format(shortMonthDayAndYear)}`;
};

export const getFormattedDurationForLegend = (durationObject: DatesRange): string =>
    durationObject?.to?.local().format(shortMonthDayAndYear);

const CPC_FILTER_NAME = "cpc";

export const buildRangeFilter = (urlParams: RawParams): string => {
    const { rangeFilter, cpcFromValue, positions, cpcToValue, previousPosition, competitorsPosition } = urlParams;

    const managedFromCPCValue = cpcFromValue ?? 0;

    const cpcFilter =
        (managedFromCPCValue || cpcToValue) && `${CPC_FILTER_NAME},${[cpcFromValue, cpcToValue].join(",")}`;

    const positionsFilter = positions?.includes("-") && `position,${positions.split("-").join(",")}`;

    const previousPositionsFilter =
        previousPosition?.includes("-") && `previousPosition,${previousPosition.split("-").join(",")}`;

    const competitorsPositionFilter =
        competitorsPosition?.includes("-") && `competitorsPosition,${competitorsPosition.split("-").join(",")}`;

    const filterList = [rangeFilter, cpcFilter, positionsFilter, previousPositionsFilter, competitorsPositionFilter];

    return filterList
        .filter((filter) => filter)
        .join("|")
        .trim();
};

export const getApiParams = (
    durationObject: DatesRange,
    currentCampaignConfiguration: IRTCurrentCampaignData,
    urlParams: RTRawParams,
    granularity: GranularityNames | undefined,
    sites: string | string[],
    hideRangeFilter?: boolean,
): IRankTrackerWidgetsSharedParams => {
    const {
        device,
        intentsFilter,
        excludeUrls,
        includeUrls,
        locationsIds,
        serpFilterGroup,
        serpFilterTypes,
        tags,
        excludeTags,
        untaggedFilter,
        withUntagged,
        excludeKeywords,
        includeKeywords,
        MainSitePositionTrend,
    } = urlParams;

    if (!currentCampaignConfiguration?.id) {
        return;
    }
    const campaignguid: string = currentCampaignConfiguration.id;
    const from = durationObject.from.local().format(apiFormat);
    const to = durationObject.to.local().format(apiFormat);
    const deviceID = device === commonWebSources.mobileWeb().id ? "Mobile" : device;
    const rangeFilter = hideRangeFilter ? null : buildRangeFilter(urlParams);
    let managedLocationsIds = locationsIds;
    if (!locationsIds) {
        managedLocationsIds = getLocationsIdsForSelectedCampaign(
            currentCampaignConfiguration.scrapingConfigurations,
            deviceID,
        );
    }

    const scrapingConfigurationGuids = getRelevantScrapingConfigIDsForSelectedDevice(
        deviceID,
        currentCampaignConfiguration.scrapingConfigurations,
        managedLocationsIds,
    );
    const invertedSerpFilter = Number(serpFilterGroup) === ESerpGroups.featuresNotOnSerp;
    const mainSiteSerpFilter = Number(serpFilterGroup) === ESerpGroups.mainSiteFeaturesOnSerp;

    const newParams: IRankTrackerWidgetsSharedParams = {
        campaignguid,
        from,
        to,
        sites,
        rangeFilter,
        intentsFilter,
        excludeUrls,
        includeUrls,
        tags,
        excludeTags,
        untaggedFilter: untaggedFilter ?? (withUntagged === "true" ? "include" : undefined),
        excludeKeywords: excludeKeywords,
        includeKeywords: includeKeywords,
        scrapingConfigurationGuids,
        granularity: granularity?.toLowerCase(),
        isDaily: true,
        serpFilter: serpFilterTypes,
        invertedSerpFilter,
        mainSiteSerpFilter,
        MainSitePositionTrend,
    };

    return newParams;
};

export const transformDisplayedURL = (url: string) => {
    let urlDecode: string;
    try {
        urlDecode = decodeURIComponent(url);
    } catch (e) {
        urlDecode = url;
    }
    return urlDecode?.replaceAll(" ", "%20");
};
